import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { toast } from 'sonner';
import { api, formatDateTime } from '../lib/api';
import { esMovil } from '../lib/utils';
import { PageHeader, EmptyState } from '../components/Common';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import {
  Mail, Inbox, Send, RefreshCw, Loader2, Search, PenSquare, ArrowLeft,
  Reply, Paperclip, PlugZap, Signature, Forward, MailOpen, MailCheck, HardDrive,
  Trash2, Folder, ChevronLeft,
} from 'lucide-react';
import { useAuth } from '../lib/auth';
import { isAdmin } from '../lib/roles';
import { PropuestaCard } from './AgentePage';
import AgenteIcono from '../components/AgenteIcono';
import MarcaBuzon, { marcaBuzon } from '../components/MarcaBuzon';

/**
 * Bandeja de Mail (2026-07-25): los dos buzones de contacto (clínica en
 * Google Workspace e instituto en SiteGround) leídos por IMAP y contestados
 * por SMTP desde ClinicPilot. Mismo espíritu que la bandeja de WhatsApp:
 * lista + lectura + respuesta, sin salir de la app.
 *
 * `?mensaje=<id>` abre ese correo al entrar (28/09/2026): es el enlace de la
 * ficha de un lead que entró solo desde un formulario web.
 */

/** Qué pasó con el lead de un formulario web (services/leads_desde_correo). */
function LeadDelFormulario({ anotacion }) {
  if (!anotacion?.resultado) return null;
  const creado = anotacion.resultado === 'creado';
  return (
    <div className="text-xs text-muted-foreground" data-testid="mail-lead-formulario">
      {anotacion.motivo || (creado ? 'Lead creado en Leads.' : '')}
      {anotacion.lead_id ? (
        <>{' '}<Link to="/leads" className="underline hover:text-foreground">Ver Leads</Link></>
      ) : null}
    </div>
  );
}

function fechaCorta(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    const hoy = new Date();
    if (d.toDateString() === hoy.toDateString()) {
      return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
    }
    return d.toLocaleDateString('es-ES', { day: 'numeric', month: 'short' });
  } catch { return ''; }
}

/** El HTML del correo se pinta en un iframe con sandbox total: sin scripts,
 *  sin formularios y sin navegación. Además, por defecto se bloquean las
 *  imágenes remotas (los píxeles de rastreo avisan al remitente de que has
 *  abierto el correo); un botón las carga si hacen falta. Los estilos
 *  inyectados evitan el scroll horizontal de los correos maquetados a 600px
 *  en pantallas de móvil. */
function CuerpoHtml({ html }) {
  const [conImagenes, setConImagenes] = useState(false);
  const csp = conImagenes
    ? ''
    : '<meta http-equiv="Content-Security-Policy" content="default-src \'none\'; style-src \'unsafe-inline\'; img-src data: cid:;">';
  const doc = `<!doctype html><meta name="viewport" content="width=device-width, initial-scale=1">${csp}` +
    '<style>body{margin:8px;font-family:system-ui,sans-serif;overflow-wrap:anywhere}' +
    'img{max-width:100%;height:auto}table{max-width:100%!important;width:auto!important}</style>' + html;
  return (
    <div className="space-y-2">
      {!conImagenes && (
        <button type="button" onClick={() => setConImagenes(true)}
          className="text-xs text-[#2E6E5E] underline underline-offset-2"
          data-testid="mail-cargar-imagenes">
          Cargar imágenes remotas
        </button>
      )}
      <iframe
        key={conImagenes ? 'img' : 'noimg'}
        title="mensaje"
        sandbox=""
        srcDoc={doc}
        className="w-full min-h-[50vh] bg-white rounded border border-border"
        data-testid="mail-body-html"
      />
    </div>
  );
}

/** Firma corporativa del buzón: texto que se precarga al final de cada
 *  correo nuevo o respuesta (editable antes de enviar). */
function FirmaDialog({ open, onOpenChange, cuentas, onSaved }) {
  const [key, setKey] = useState('clinica');
  const [firma, setFirma] = useState('');
  const [guardando, setGuardando] = useState(false);
  const cuenta = cuentas.find((c) => c.key === key);

  useEffect(() => { if (open) setKey(cuentas[0]?.key || 'clinica'); }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (open) setFirma(cuenta?.firma || ''); }, [open, key, cuenta?.firma]); // eslint-disable-line react-hooks/exhaustive-deps

  const guardar = async () => {
    setGuardando(true);
    try {
      await api.put(`/api/mail/cuentas/${key}/firma`, { firma });
      toast.success('Firma guardada');
      onOpenChange(false);
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo guardar');
    } finally {
      setGuardando(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg" data-testid="mail-firma-dialog">
        <DialogHeader>
          <DialogTitle>Firma personal</DialogTitle>
        </DialogHeader>
        {cuentas.length > 1 && (
          <div className="flex gap-1.5">
            {cuentas.map((c) => (
              <button key={c.key} type="button" onClick={() => setKey(c.key)}
                className={`inline-flex items-center gap-1.5 px-2 py-1 rounded border text-xs ${
                  key === c.key ? cuentaUi(c.key).activo : 'border-border opacity-60 hover:opacity-100'
                }`}
                data-testid={`mail-firma-tab-${c.key}`}>
                <span className={`w-2 h-2 rounded-full ${cuentaUi(c.key).punto}`} />
                {c.label}
              </button>
            ))}
          </div>
        )}
        <div className="space-y-2">
          <p className="text-xs text-muted-foreground">
            La firma corporativa (nombre, dirección, teléfono, web y aviso
            legal) ya va automática en cada correo de <strong>{cuenta?.email}</strong>.
            Aquí puedes añadir una línea personal opcional que irá justo
            encima — por ejemplo una despedida.
          </p>
          <Textarea rows={3} value={firma} onChange={(e) => setFirma(e.target.value)}
            placeholder={'Un cordial saludo,\nEl equipo de la clínica'}
            data-testid="mail-firma-text" />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button onClick={guardar} disabled={guardando}
            className="bg-[#2E6E5E] hover:bg-[#1E4A3F]" data-testid="mail-firma-save">
            {guardando && <Loader2 className="w-4 h-4 mr-1 animate-spin" />} Guardar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Color de cada buzón, elegido para que se distingan de un golpe de vista
 *  en la pantalla pequeña del móvil:
 *  - el principal: azul intenso.
 *  - el segundo: verde.
 *  Barra lateral gruesa (6px) en cada fila: es lo que de verdad se lee al
 *  ojear la bandeja en el teléfono. El NOMBRE de cada buzón no va aquí: es
 *  su etiqueta del `.env` (MAIL_<BUZÓN>_LABEL), que llega con las cuentas
 *  (V2: antes iba escrito). */
const CUENTA_UI = {
  clinica: {
    hex: '#1D4ED8',
    punto: 'bg-[#1D4ED8]',
    solido: 'bg-[#1D4ED8]',
    chip: 'bg-[#1D4ED8]/15 text-[#1D4ED8] dark:text-[#93B4FF] border-[#1D4ED8]/60',
    activo: 'border-[#1D4ED8] bg-[#1D4ED8]/10 ring-2 ring-[#1D4ED8]',
    barra: 'border-l-[6px] border-l-[#1D4ED8]',
    fondoNoLeido: 'bg-[#1D4ED8]/[0.06]',
  },
  instituto: {
    hex: '#12724A',
    punto: 'bg-[#12724A]',
    solido: 'bg-[#12724A]',
    chip: 'bg-[#12724A]/15 text-[#0A2C1F] dark:text-[#7DD8AC] border-[#12724A]/50',
    activo: 'border-[#12724A] bg-[#12724A]/[0.08] ring-2 ring-[#12724A]',
    barra: 'border-l-[6px] border-l-[#12724A]',
    fondoNoLeido: 'bg-[#16A34A]/[0.12]',
  },
};
const cuentaUi = (key) => CUENTA_UI[key] || CUENTA_UI.clinica;

/** Los adjuntos viejos se guardaron como texto y los nuevos como objeto:
 *  se normaliza a {nombre, tipo, size} para pintar unos y otros. */
const normalizaAdjuntos = (lista) => (lista || []).map((a) =>
  (typeof a === 'string' ? { nombre: a } : a));

const fmtSize = (n) => {
  if (!n && n !== 0) return '';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
};

/** Cita el mensaje original al reenviarlo, al estilo Gmail. */
function citaReenvio(m) {
  const de = m.from_name ? `${m.from_name} <${m.from_email}>` : m.from_email;
  return [
    '', '', '---------- Mensaje reenviado ----------',
    `De: ${de}`,
    `Fecha: ${m.date || ''}`,
    `Asunto: ${m.subject || '(sin asunto)'}`,
    `Para: ${m.to || ''}`,
    '',
    m.body_text || '',
  ].join('\n');
}

/** Selector de archivos de ClinicPilot Drive para adjuntarlos a un correo:
 *  navegación por carpetas + búsqueda, selección múltiple. */
function DrivePicker({ open, onOpenChange, onElegidos }) {
  const [folderId, setFolderId] = useState(null);
  const [ruta, setRuta] = useState([]);   // migas: [{id, name}]
  const [datos, setDatos] = useState({ folders: [], docs: [] });
  const [sel, setSel] = useState([]);
  const [q, setQ] = useState('');

  useEffect(() => {
    if (!open) return;
    setSel([]); setQ(''); setFolderId(null); setRuta([]);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const params = {};
    if (q.trim()) params.q = q.trim();
    else if (folderId) params.folder_id = folderId;
    api.get('/api/drive', { params })
      .then((r) => setDatos({ folders: r.data?.folders || [], docs: r.data?.docs || [] }))
      .catch(() => setDatos({ folders: [], docs: [] }));
  }, [open, folderId, q]);

  const toggle = (d) => setSel((prev) => (
    prev.some((x) => x.id === d.id) ? prev.filter((x) => x.id !== d.id) : [...prev, d]
  ));

  const entrar = (f) => { setQ(''); setFolderId(f.id); setRuta((r) => [...r, { id: f.id, name: f.name }]); };
  const atras = () => {
    setRuta((r) => {
      const nueva = r.slice(0, -1);
      setFolderId(nueva.length ? nueva[nueva.length - 1].id : null);
      return nueva;
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg" data-testid="mail-drive-picker">
        <DialogHeader>
          <DialogTitle>Adjuntar del Drive</DialogTitle>
        </DialogHeader>
        <div className="space-y-2">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input className="pl-8 h-9" placeholder="Buscar en todo el Drive…"
              value={q} onChange={(e) => setQ(e.target.value)} data-testid="mail-drive-buscar" />
          </div>
          {!q.trim() && (
            <div className="flex items-center gap-1 text-xs text-muted-foreground">
              {ruta.length > 0 && (
                <button type="button" onClick={atras} className="inline-flex items-center hover:text-[#13231E]">
                  <ChevronLeft className="w-3.5 h-3.5" /> Atrás
                </button>
              )}
              <span className="truncate">
                {['Drive', ...ruta.map((r) => r.name)].join(' / ')}
              </span>
            </div>
          )}
          <div className="border border-border rounded max-h-[45vh] overflow-y-auto divide-y divide-border">
            {!q.trim() && datos.folders.map((f) => (
              <button key={f.id} type="button" onClick={() => entrar(f)}
                className="w-full flex items-center gap-2 px-3 py-2 text-sm text-left hover:bg-muted/40"
                data-testid="mail-drive-carpeta">
                <Folder className="w-4 h-4 text-[#2E6E5E] shrink-0" />
                <span className="truncate">{f.name}</span>
              </button>
            ))}
            {datos.docs.map((d) => {
              const marcado = sel.some((x) => x.id === d.id);
              return (
                <button key={d.id} type="button" onClick={() => toggle(d)}
                  className={`w-full flex items-center gap-2 px-3 py-2 text-sm text-left hover:bg-muted/40 ${marcado ? 'bg-[#2E6E5E]/10' : ''}`}
                  data-testid="mail-drive-doc">
                  <input type="checkbox" readOnly checked={marcado} className="accent-[#2E6E5E] shrink-0" />
                  <span className="flex-1 min-w-0 truncate">{d.name}</span>
                  <span className="text-xs text-muted-foreground shrink-0">{fmtSize(d.size)}</span>
                </button>
              );
            })}
            {datos.folders.length === 0 && datos.docs.length === 0 && (
              <div className="px-3 py-6 text-center text-sm text-muted-foreground">
                {q.trim() ? 'Sin resultados.' : 'Carpeta vacía.'}
              </div>
            )}
          </div>
        </div>
        <DialogFooter className="flex-row justify-end gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button disabled={sel.length === 0}
            onClick={() => { onElegidos(sel); onOpenChange(false); }}
            className="bg-[#2E6E5E] hover:bg-[#1E4A3F]" data-testid="mail-drive-anadir">
            Adjuntar {sel.length > 0 ? `(${sel.length})` : ''}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ComposeDialog({ open, onOpenChange, cuentas, seleccionadas, admin, replyTo, forwardOf, onSent }) {
  const [para, setPara] = useState('');
  const [asunto, setAsunto] = useState('');
  const [cuerpo, setCuerpo] = useState('');
  const [archivos, setArchivos] = useState([]);
  const [driveDocs, setDriveDocs] = useState([]);
  const [drivePickerOpen, setDrivePickerOpen] = useState(false);
  const [remitenteKey, setRemitenteKey] = useState('clinica');
  const [enviando, setEnviando] = useState(false);
  const fileRef = useRef(null);

  const conectables = cuentas.filter((c) => c.conectada);
  const remitente = cuentas.find((c) => c.key === remitenteKey);

  useEffect(() => {
    if (!open) return;
    // La firma corporativa se añade sola al enviar: aquí solo el mensaje.
    if (replyTo) {
      setPara(replyTo.from_email || '');
      const asu = replyTo.subject || '';
      setAsunto(/^re:/i.test(asu) ? asu : `Re: ${asu}`);
      setCuerpo('');
    } else if (forwardOf) {
      setPara('');
      const asu = forwardOf.subject || '';
      setAsunto(/^fwd?:/i.test(asu) ? asu : `Fwd: ${asu}`);
      setCuerpo(citaReenvio(forwardOf));
    } else {
      setPara(''); setAsunto(''); setCuerpo('');
    }
    setArchivos([]);
    setDriveDocs([]);
    // Respuestas y reenvíos salen del buzón que recibió el correo; en los
    // nuevos, del primer buzón activo conectado.
    setRemitenteKey(
      replyTo?.account || forwardOf?.account
      || (seleccionadas || []).find((k) => conectables.some((c) => c.key === k))
      || conectables[0]?.key || 'clinica'
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, replyTo, forwardOf]);

  const enviar = async () => {
    if (!para.trim() || !cuerpo.trim()) {
      toast.error('Faltan el destinatario o el mensaje');
      return;
    }
    setEnviando(true);
    try {
      // Multipart: los adjuntos viajan junto al texto en la misma petición.
      const fd = new FormData();
      fd.append('cuenta', remitenteKey);
      fd.append('para', para.trim());
      fd.append('asunto', asunto.trim());
      fd.append('cuerpo', cuerpo);
      if (replyTo?.id) fd.append('responde_a', replyTo.id);
      if (forwardOf?.id) fd.append('reenvia_a', forwardOf.id);
      archivos.forEach((f) => fd.append('archivos', f));
      if (driveDocs.length > 0) fd.append('drive_ids', driveDocs.map((d) => d.id).join(','));
      await api.post('/api/mail/enviar', fd);
      toast.success('Enviado');
      onOpenChange(false);
      onSent?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo enviar');
    } finally {
      setEnviando(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-xl" data-testid="mail-compose-dialog">
        <DialogHeader>
          <DialogTitle>{replyTo ? 'Responder' : forwardOf ? 'Reenviar' : 'Redactar'}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="text-xs text-muted-foreground flex items-center gap-2 flex-wrap">
            <span>De:</span>
            {!replyTo && conectables.length > 1 ? (
              conectables.map((c) => (
                <button key={c.key} type="button" onClick={() => setRemitenteKey(c.key)}
                  className={`inline-flex items-center gap-1.5 px-2 py-1 rounded border text-xs ${
                    remitenteKey === c.key ? cuentaUi(c.key).activo : 'border-border opacity-60 hover:opacity-100'
                  }`}
                  data-testid={`mail-compose-de-${c.key}`}>
                  <span className={`w-2 h-2 rounded-full ${cuentaUi(c.key).punto}`} />
                  {c.email}
                </button>
              ))
            ) : (
              <strong className="text-[#13231E]">{remitente?.email}</strong>
            )}
          </div>
          <div className="space-y-1">
            <Label>Para</Label>
            <Input value={para} onChange={(e) => setPara(e.target.value)}
              placeholder="destinatario@ejemplo.com" data-testid="mail-compose-to" />
          </div>
          <div className="space-y-1">
            <Label>Asunto</Label>
            <Input value={asunto} onChange={(e) => setAsunto(e.target.value)}
              data-testid="mail-compose-subject" />
          </div>
          <div className="space-y-1">
            <Label>Mensaje</Label>
            <Textarea rows={7} value={cuerpo} onChange={(e) => setCuerpo(e.target.value)}
              data-testid="mail-compose-body" />
            {/* La firma del buzón elegido, colocada tal cual saldrá en el
                envío. Es HTML generado por nuestro propio backend (datos
                escapados allí), no contenido de terceros. */}
            <div className="border border-border rounded p-2.5 bg-white overflow-x-auto"
              data-testid="mail-compose-firma-preview">
              <div className="text-[10px] uppercase tracking-wide text-muted-foreground">
                Firma de {remitente?.label || 'este buzón'} — se añade sola al enviar
              </div>
              {remitente?.firma && (
                <div className="text-sm text-[#1c2422] whitespace-pre-wrap mt-1">{remitente.firma}</div>
              )}
              <div className="[&_table]:!mt-2"
                dangerouslySetInnerHTML={{ __html: remitente?.firma_html || '' }} />
            </div>
          </div>

          {/* Adjuntos */}
          <input
            ref={fileRef} type="file" multiple className="hidden"
            onChange={(e) => {
              const nuevos = Array.from(e.target.files || []);
              const total = [...archivos, ...nuevos];
              if (total.reduce((s, f) => s + f.size, 0) > 15 * 1024 * 1024) {
                toast.error('Los adjuntos superan los 15 MB en total');
              } else {
                setArchivos(total.slice(0, 10));
              }
              e.target.value = '';
            }}
            data-testid="mail-compose-file-input"
          />
          <div className="flex flex-wrap items-center gap-1.5">
            <Button type="button" size="sm" variant="outline" onClick={() => fileRef.current?.click()}
              data-testid="mail-compose-attach" title="Adjuntar archivos de tu ordenador">
              <Paperclip className="w-3.5 h-3.5 mr-1" /> Adjuntar
            </Button>
            {admin && (
              <Button type="button" size="sm" variant="outline" onClick={() => setDrivePickerOpen(true)}
                data-testid="mail-compose-attach-drive"
                title="Adjuntar documentos guardados en ClinicPilot Drive">
                <HardDrive className="w-3.5 h-3.5 mr-1" /> Del Drive
              </Button>
            )}
            {driveDocs.map((d, i) => (
              <span key={d.id}
                className="inline-flex items-center gap-1 max-w-full text-xs bg-[#2E6E5E]/10 rounded px-2 py-1"
                data-testid="mail-compose-adjunto-drive">
                <HardDrive className="w-3 h-3 shrink-0 text-[#2E6E5E]" />
                <span className="truncate max-w-[10rem]">{d.name}</span>
                <span className="text-muted-foreground shrink-0">{fmtSize(d.size)}</span>
                <button type="button" title="Quitar este adjunto"
                  className="inline-flex items-center justify-center w-8 h-8 -my-1.5 -mr-1 text-destructive text-base"
                  onClick={() => setDriveDocs((prev) => prev.filter((x) => x.id !== d.id))}
                  aria-label={`Quitar ${d.name}`}>
                  ×
                </button>
              </span>
            ))}
            {archivos.map((f, i) => (
              <span key={i}
                className="inline-flex items-center gap-1 max-w-full text-xs bg-muted/60 rounded px-2 py-1"
                data-testid="mail-compose-adjunto">
                <Paperclip className="w-3 h-3 shrink-0" />
                <span className="truncate max-w-[10rem]">{f.name}</span>
                <span className="text-muted-foreground shrink-0">{fmtSize(f.size)}</span>
                <button type="button" title="Quitar este adjunto"
                  className="inline-flex items-center justify-center w-8 h-8 -my-1.5 -mr-1 text-destructive text-base"
                  onClick={() => setArchivos((prev) => prev.filter((_, idx) => idx !== i))}
                  aria-label={`Quitar ${f.name}`}>
                  ×
                </button>
              </span>
            ))}
          </div>
        </div>
        <DialogFooter className="flex-row justify-end gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button onClick={enviar} disabled={enviando}
            className="bg-[#2E6E5E] hover:bg-[#1E4A3F]" data-testid="mail-compose-send">
            {enviando ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Send className="w-4 h-4 mr-1" />}
            Enviar
          </Button>
        </DialogFooter>
        <DrivePicker
          open={drivePickerOpen}
          onOpenChange={setDrivePickerOpen}
          onElegidos={(docs) => setDriveDocs((prev) => {
            const nuevos = docs.filter((d) => !prev.some((x) => x.id === d.id));
            return [...prev, ...nuevos].slice(0, 10);
          })}
        />
      </DialogContent>
    </Dialog>
  );
}

export default function MailPage() {
  const { user } = useAuth();
  // Guardar en Drive es cosa de administradores, igual que el propio Drive.
  const admin = isAdmin(user);
  const [cuentas, setCuentas] = useState([]);
  // Ambos buzones activos por defecto: bandeja unificada. Se puede dejar
  // solo uno pulsando el otro (siempre queda al menos uno activo).
  const [seleccionadas, setSeleccionadas] = useState(['clinica', 'instituto']);
  const [carpeta, setCarpeta] = useState('in');
  const [q, setQ] = useState('');
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [cargando, setCargando] = useState(false);
  const [sincronizando, setSincronizando] = useState(false);
  const [abierto, setAbierto] = useState(null);   // mensaje completo
  // El enlace de un lead al correo del que salió (28/09/2026).
  const [searchParams, setSearchParams] = useSearchParams();
  const mensajeDelEnlace = searchParams.get('mensaje') || '';
  const [compose, setCompose] = useState(false);
  const [replyTo, setReplyTo] = useState(null);
  const [forwardOf, setForwardOf] = useState(null);
  const [firmaOpen, setFirmaOpen] = useState(false);
  // Caja del Agente IA dentro de Mail (solo administradores): la misma IA
  // del menú Agente; la conversación queda también allí.
  const [iaTexto, setIaTexto] = useState('');
  const [iaMsg, setIaMsg] = useState(null); // respuesta completa del agente
  const [iaCargando, setIaCargando] = useState(false);
  const [iaEnviandoProp, setIaEnviandoProp] = useState(false);

  const preguntarIA = async () => {
    const t = iaTexto.trim();
    if (!t || iaCargando) return;
    setIaCargando(true);
    try {
      const r = await api.post('/api/agente/chat', { texto: t });
      setIaTexto('');
      const resp = r.data?.respuesta || null;
      // Desde el refactor de 2026-08-17 el chat responde al momento con un
      // mensaje VACÍO en estado «trabajando» y lo rellena en segundo plano;
      // este cuadro seguía esperándolo relleno y por eso «no respondía en
      // correo» (2026-08-24, dirección). Se sondea hasta que esté.
      if (resp && resp.estado === 'trabajando') {
        setIaMsg({ ...resp, texto: 'Pensando…' });
        let hecho = null;
        for (let i = 0; i < 120 && !hecho; i += 1) {
          // eslint-disable-next-line no-await-in-loop
          await new Promise((res) => setTimeout(res, i < 6 ? 800 : 1500));
          // eslint-disable-next-line no-await-in-loop
          const e = await api.get('/api/agente/mensajes');
          const m = (Array.isArray(e.data) ? e.data : []).find((x) => x.id === resp.id);
          if (m && m.estado !== 'trabajando') hecho = m;
        }
        if (!hecho) throw new Error('El Agente tardó demasiado');
        setIaMsg(hecho);
      } else {
        setIaMsg(resp);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'El Agente no pudo responder');
    } finally {
      setIaCargando(false);
    }
  };

  const iaEnviarPropuesta = async (chatId) => {
    setIaEnviandoProp(true);
    try {
      await api.post(`/api/agente/enviar/${chatId}`);
      toast.success('Correo enviado por el Agente');
      setIaMsg((m) => (m ? { ...m, propuesta_estado: 'enviada' } : m));
      if (carpeta === 'out') cargarLista();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo enviar');
    } finally {
      setIaEnviandoProp(false);
    }
  };

  const iaDescartarPropuesta = async (chatId) => {
    try {
      await api.post(`/api/agente/propuesta/${chatId}/descartar`);
      setIaMsg((m) => (m ? { ...m, propuesta_estado: 'descartada' } : m));
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo descartar');
    }
  };

  const selKey = seleccionadas.join(',');
  const cuentasSel = useMemo(
    () => cuentas.filter((c) => seleccionadas.includes(c.key)), [cuentas, seleccionadas]);
  const conectadas = useMemo(
    () => cuentasSel.filter((c) => c.conectada), [cuentasSel]);
  const sinLeerSel = cuentasSel.reduce((s, c) => s + (c.sin_leer || 0), 0);

  const toggleCuenta = (key) => {
    setAbierto(null);
    setSeleccionadas((prev) => {
      if (prev.includes(key)) {
        // Siempre queda al menos un buzón activo.
        return prev.length > 1 ? prev.filter((k) => k !== key) : prev;
      }
      return [...prev, key].sort();
    });
  };

  const cargarCuentas = useCallback(() => {
    api.get('/api/mail/cuentas')
      .then((r) => setCuentas(Array.isArray(r.data) ? r.data : []))
      .catch(() => {});
  }, []);

  const cargarLista = useCallback(() => {
    setCargando(true);
    const params = { cuenta: selKey, carpeta };
    if (q.trim()) params.q = q.trim();
    api.get('/api/mail/mensajes', { params })
      .then((r) => { setItems(r.data?.items || []); setTotal(r.data?.total || 0); })
      .catch(() => {})
      .finally(() => setCargando(false));
  }, [selKey, carpeta, q]);

  useEffect(() => { cargarCuentas(); }, [cargarCuentas]);
  useEffect(() => {
    const t = setTimeout(cargarLista, q ? 300 : 0);
    return () => clearTimeout(t);
  }, [cargarLista, q]);

  const sincronizar = useCallback(async (silencioso = false) => {
    if (conectadas.length === 0) return;
    // «En silencio» de verdad: el sondeo de fondo no enciende el spinner
    // del botón Actualizar (giraba solo cada 60 s, 2026-08-21).
    if (!silencioso) setSincronizando(true);
    try {
      // Un sync por buzón activo, en paralelo.
      const resultados = await Promise.allSettled(
        conectadas.map((c) => api.post(`/api/mail/cuentas/${c.key}/sincronizar`)));
      const n = resultados.reduce(
        (s, r) => s + (r.status === 'fulfilled' ? (r.value.data?.nuevos || 0) : 0), 0);
      // Correos que el sync ha encontrado ya leídos en Gmail y ha apagado aquí
      // (2026-09-12). Cambian la lista sin que entre nada nuevo.
      const leidosFuera = resultados.reduce(
        (s, r) => s + (r.status === 'fulfilled' ? (r.value.data?.leidos_fuera || 0) : 0), 0);
      const fallo = resultados.find((r) => r.status === 'rejected');
      if (!silencioso) {
        if (fallo && n === 0) {
          toast.error(fallo.reason?.response?.data?.detail || 'No se pudo sincronizar');
        } else if (n) {
          toast.success(`${n} correo${n === 1 ? '' : 's'} nuevo${n === 1 ? '' : 's'}`);
        } else {
          // Si no ha entrado nada pero la lista ha cambiado, se dice por qué:
          // si no, parece que los correos se marcan solos.
          toast.success(leidosFuera
            ? `Sin correos nuevos · ${leidosFuera} ya leído${leidosFuera === 1 ? '' : 's'} en el buzón`
            : 'Sin correos nuevos');
        }
      }
      // La lista solo se recarga si algo ha cambiado (o lo pediste a mano):
      // el refresco de fondo ya no pisa la carpeta o búsqueda que estés viendo.
      if (n > 0 || leidosFuera > 0 || !silencioso) {
        cargarLista();
        cargarCuentas();
      }
    } finally {
      setSincronizando(false);
    }
  }, [conectadas, cargarLista, cargarCuentas]);

  // El intervalo llama siempre a la versión fresca vía ref: la closure del
  // efecto se quedaba con la carpeta/búsqueda del primer render.
  const sincronizarRef = useRef(sincronizar);
  useEffect(() => { sincronizarRef.current = sincronizar; }, [sincronizar]);

  // Al entrar o cambiar de cuenta: sincroniza en silencio. Después, cada 60 s
  // si la pestaña está a la vista (el correo no necesita el ritmo del chat).
  useEffect(() => {
    sincronizarRef.current(true);
    const id = setInterval(() => { if (!document.hidden) sincronizarRef.current(true); }, 60000);
    return () => clearInterval(id);
  }, [selKey, conectadas.length]);

  const abrir = async (m) => {
    try {
      const r = await api.get(`/api/mail/mensajes/${m.id}`);
      // Si mientras llegaba la respuesta se cambió de buzón, no se abre.
      if (r.data?.account && !seleccionadas.includes(r.data.account)) return;
      setAbierto(r.data);
      if (!m.read) {
        setItems((prev) => prev.map((x) => (x.id === m.id ? { ...x, read: true } : x)));
        cargarCuentas();
      }
    } catch {
      toast.error('No se pudo abrir el mensaje');
    }
  };

  // Llegar con `?mensaje=…`: se abre ese correo, con su buzón a la vista (el
  // que dice el propio correo, no la dirección), y se quita de la dirección
  // para que recargar no lo vuelva a abrir.
  useEffect(() => {
    if (!mensajeDelEnlace) return;
    api.get(`/api/mail/mensajes/${encodeURIComponent(mensajeDelEnlace)}`)
      .then((r) => {
        const m = r.data;
        if (!m?.id) throw new Error('sin correo');
        if (m.account) setSeleccionadas((prev) => (prev.includes(m.account) ? prev : [...prev, m.account].sort()));
        setAbierto(m);
        cargarCuentas();
      })
      .catch(() => toast.error('Ese correo ya no está en la bandeja'));
    setSearchParams({}, { replace: true });
  }, [mensajeDelEnlace, setSearchParams, cargarCuentas]);

  const responder = () => { setReplyTo(abierto); setForwardOf(null); setCompose(true); };
  const reenviar = () => { setForwardOf(abierto); setReplyTo(null); setCompose(true); };
  const redactar = () => { setReplyTo(null); setForwardOf(null); setCompose(true); };

  const [guardandoDrive, setGuardandoDrive] = useState(null); // índice en curso
  const [marcados, setMarcados] = useState([]); // ids seleccionados en la lista

  // Al cambiar de buzón o carpeta, la selección deja de tener sentido.
  useEffect(() => { setMarcados([]); }, [selKey, carpeta]);

  const toggleMarcado = (id) => setMarcados((prev) => (
    prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
  ));

  const eliminarIds = async (ids) => {
    if (!ids.length) return;
    const msj = ids.length === 1
      ? '¿Quitar este correo de la bandeja?'
      : `¿Quitar estos ${ids.length} correos de la bandeja?`;
    if (!window.confirm(`${msj} El original sigue intacto en el buzón (Gmail/webmail).`)) return;
    try {
      await api.post('/api/mail/mensajes/eliminar', { ids });
      toast.success(ids.length === 1 ? 'Correo eliminado de la bandeja' : `${ids.length} correos eliminados`);
      setMarcados([]);
      setAbierto(null);
      cargarLista();
      cargarCuentas();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo eliminar');
    }
  };

  const guardarEnDrive = async (i, adj) => {
    setGuardandoDrive(i);
    try {
      const r = await api.post(`/api/mail/mensajes/${abierto.id}/adjuntos/${i}/a-drive`);
      toast.success(`${adj.nombre} guardado en Drive (carpeta ${r.data?.carpeta || 'Correo'})`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo guardar en Drive');
    } finally {
      setGuardandoDrive(null);
    }
  };

  const descargarAdjunto = async (i, adj) => {
    // En móvil: pestaña nueva con el visor nativo (el truco del blob dejaba
    // la app bloqueada en iOS al volver atrás).
    if (esMovil()) {
      const base = process.env.REACT_APP_BACKEND_URL || '';
      window.open(`${base}/api/mail/mensajes/${abierto.id}/adjuntos/${i}`, '_blank', 'noopener');
      return;
    }
    try {
      const r = await api.get(`/api/mail/mensajes/${abierto.id}/adjuntos/${i}`, { responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = adj.nombre || `adjunto-${i}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    } catch {
      toast.error('No se pudo descargar el adjunto');
    }
  };

  /** Marca leído/no leído uno o varios correos (aquí y en el buzón). */
  const marcarLeido = async (ids, leido, { cerrar = false } = {}) => {
    if (!ids.length) return;
    try {
      await api.post('/api/mail/mensajes/leido', { ids, leido });
      setItems((prev) => prev.map((x) => (ids.includes(x.id) ? { ...x, read: leido } : x)));
      if (cerrar) setAbierto(null);
      else if (abierto && ids.includes(abierto.id)) setAbierto((a) => ({ ...a, read: leido }));
      setMarcados([]);
      cargarCuentas();
      const n = ids.length;
      toast.success(n === 1
        ? (leido ? 'Marcado como leído' : 'Marcado como no leído')
        : `${n} correos marcados como ${leido ? 'leídos' : 'no leídos'}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo marcar');
    }
  };

  return (
    <div className="space-y-4">
      <PageHeader
        title="Mail"
        subtitle="Los buzones de contacto de la clínica y el instituto, sin salir de ClinicPilot."
        actions={(
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" onClick={() => setFirmaOpen(true)}
              disabled={cuentas.length === 0} data-testid="mail-firma-btn"
              title="Línea personal que va encima de la firma corporativa">
              <Signature className="w-4 h-4 mr-1" /> Firma
            </Button>
            <Button variant="outline" onClick={() => sincronizar(false)}
              disabled={conectadas.length === 0 || sincronizando} data-testid="mail-sync-btn"
              title="Buscar correo nuevo ahora">
              {sincronizando
                ? <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                : <RefreshCw className="w-4 h-4 mr-1" />}
              Actualizar
            </Button>
            <Button onClick={redactar} disabled={conectadas.length === 0}
              className="bg-[#2E6E5E] hover:bg-[#1E4A3F]" data-testid="mail-compose-btn"
              title="Escribir un correo nuevo">
              <PenSquare className="w-4 h-4 mr-1" /> Redactar
            </Button>
          </div>
        )}
      />

      {/* Selector de buzones (rediseñado 2026-07-27): cada uno con SU marca —
          el logotipo de la clínica y el monograma del Instituto — en vez del
          puntito de color, que no decía de quién era el buzón.
          Se pueden tener los dos activos a la vez (bandeja unificada). Activo =
          tinte de la marca y anillo de color; apagado = blanco y texto suave,
          sin `opacity` global (atenuaba también el logo y se veía sucio). */}
      <div className="flex flex-wrap gap-2.5" data-testid="mail-accounts">
        {cuentas.map((c) => {
          const marca = marcaBuzon(c.key);
          const activa = seleccionadas.includes(c.key);
          return (
            <button
              key={c.key}
              type="button"
              onClick={() => toggleCuenta(c.key)}
              aria-pressed={activa}
              title={activa ? 'Pulsar para ocultar este buzón' : 'Pulsar para mostrar este buzón'}
              className={`group flex items-center gap-3 rounded-xl px-3 py-2.5 text-left
                min-h-[56px] transition-[background-color,box-shadow] duration-150 ring-1
                focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2
                focus-visible:ring-[#2E6E5E] ${
                activa
                  ? `${marca.tinte} ring-2 ${marca.anillo} elev-2`
                  : 'bg-white dark:bg-card ring-border hover:ring-[#2E6E5E]/30 hover:bg-muted/30 elev-1'
              }`}
              data-testid={`mail-account-${c.key}`}
            >
              <MarcaBuzon clave={c.key} cuenta={c} size={44}
                className={activa ? '' : 'grayscale-[0.35] transition-[filter] duration-150 group-hover:grayscale-0'} />
              <span className="min-w-0">
                <span className="flex items-center gap-1.5">
                  <span className={`font-semibold truncate ${activa ? 'text-[#13231E]' : 'text-muted-foreground'}`}>
                    {c.label}
                  </span>
                  {c.sin_leer > 0 && (
                    <span className={`shrink-0 min-w-[20px] h-5 px-1.5 rounded-full text-[11px]
                      font-bold text-white inline-flex items-center justify-center ${marca.fondo}`}>
                      {c.sin_leer}
                    </span>
                  )}
                </span>
                <span className="block text-xs text-muted-foreground truncate">{c.email}</span>
              </span>
            </button>
          );
        })}
      </div>

      {/* Pregúntale al Agente sin salir del correo (solo administradores). */}
      {admin && (
        <div className="cjo-card p-2.5 space-y-2" data-testid="mail-ia-box">
          <div className="flex items-center gap-2">
            <AgenteIcono size={28} />
            <Input
              value={iaTexto}
              onChange={(e) => setIaTexto(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') preguntarIA(); }}
              placeholder="Pídele algo al Agente sobre el correo… (resumen, borrador de respuesta…)"
              className="h-9 flex-1"
              data-testid="mail-ia-input"
            />
            <Button size="sm" onClick={preguntarIA} disabled={iaCargando || !iaTexto.trim()}
              className="bg-[#2E6E5E] hover:bg-[#1E4A3F] h-9" data-testid="mail-ia-enviar"
              title="Preguntar al Agente">
              {iaCargando ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            </Button>
          </div>
          {iaMsg?.texto && (
            <div className="text-sm whitespace-pre-wrap break-words border-l-2 border-[#2E6E5E] pl-3 py-0.5 text-[#13231E]"
              data-testid="mail-ia-respuesta">
              {iaMsg.texto}
              <PropuestaCard m={iaMsg} onEnviar={iaEnviarPropuesta}
                onDescartar={iaDescartarPropuesta} enviando={iaEnviandoProp}
                etiquetas={Object.fromEntries(cuentas.map((c) => [c.key, c.label]))} />
            </div>
          )}
        </div>
      )}

      {/* Aviso, no bloqueo: aunque falte la contraseña se sigue viendo el
          correo ya descargado (si caduca, no se pierde el histórico). */}
      {cuentasSel.length > 0 && conectadas.length === 0 && (
        <div className="cjo-card p-3 flex items-start gap-2 text-sm" data-testid="mail-not-connected">
          <PlugZap className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
          <div>
            <span className="font-medium text-[#13231E]">Buzón sin conectar. </span>
            <span className="text-muted-foreground">
              Falta la contraseña de {cuentasSel.map((c) => c.email).join(' y ')} en
              el servidor: no entrará correo nuevo ni se podrá enviar, pero lo ya
              descargado se sigue viendo.
            </span>
          </div>
        </div>
      )}

      {abierto ? (
        /* ------------------------- Lectura ------------------------- */
        <div className="cjo-card overflow-hidden" data-testid="mail-reader">
          {/* Cabecera en dos filas para que el asunto y los botones no se
              pisen en pantallas pequeñas. */}
          <div className="px-3 sm:px-4 py-3 border-b border-border space-y-2">
            <div className="flex items-start gap-2">
              <Button size="icon" variant="ghost" className="shrink-0 -ml-1 h-10 w-10"
                onClick={() => setAbierto(null)} data-testid="mail-back-btn"
                title="Volver a la lista">
                <ArrowLeft className="w-4 h-4" />
              </Button>
              <div className="flex-1 min-w-0">
                <span className={`inline-flex text-[9px] font-semibold uppercase tracking-wide border rounded px-1.5 py-0.5 mb-1 ${cuentaUi(abierto.account).chip}`}
                  data-testid="mail-reader-cuenta">
                  {cuentas.find((c) => c.key === abierto.account)?.label || abierto.account}
                </span>
                <div className="font-display font-semibold text-[#13231E] break-words">
                  {abierto.subject || '(sin asunto)'}
                </div>
                <div className="text-xs text-muted-foreground break-words">
                  {abierto.dir === 'out'
                    ? <>Para: {abierto.to}</>
                    : <>{abierto.from_name ? `${abierto.from_name} <${abierto.from_email}>` : abierto.from_email}</>}
                  {' · '}{formatDateTime(abierto.date)}
                </div>
                <LeadDelFormulario anotacion={abierto.lead_formulario} />
              </div>
            </div>
            <div className="flex flex-wrap gap-1.5 justify-end">
              {abierto.dir === 'in' && (
                <>
                  <Button size="icon" variant="ghost" className="h-10 w-10"
                    /* Al marcarlo como NO leído se vuelve a la lista (si no,
                       quedaría abierto y se marcaría leído otra vez). */
                    onClick={() => marcarLeido([abierto.id], !abierto.read, { cerrar: abierto.read })}
                    title={abierto.read ? 'Marcar como no leído' : 'Marcar como leído'}
                    data-testid="mail-unread-btn">
                    {abierto.read ? <MailOpen className="w-4 h-4" /> : <MailCheck className="w-4 h-4" />}
                  </Button>
                  <Button size="sm" variant="outline" onClick={responder} data-testid="mail-reply-btn"
                    title="Responder al remitente">
                    <Reply className="w-4 h-4 mr-1" /> Responder
                  </Button>
                </>
              )}
              <Button size="sm" variant="outline" onClick={reenviar} data-testid="mail-forward-btn"
                title="Reenviar este correo a otra dirección">
                <Forward className="w-4 h-4 mr-1" /> Reenviar
              </Button>
              <Button size="sm" variant="outline" onClick={() => eliminarIds([abierto.id])}
                className="text-destructive border-destructive/40 hover:bg-destructive/10"
                title="Quitar de la bandeja (el original sigue en el buzón)"
                data-testid="mail-delete-btn">
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          </div>
          {normalizaAdjuntos(abierto.attachments).length > 0 && (
            <div className="px-3 sm:px-4 py-2 border-b border-border flex flex-wrap gap-1.5">
              {normalizaAdjuntos(abierto.attachments).map((a, i) => (
                abierto.dir === 'in' ? (
                  <span key={i} className="inline-flex items-stretch max-w-full rounded border border-border overflow-hidden">
                    <button
                      type="button" onClick={() => descargarAdjunto(i, a)}
                      className="inline-flex items-center gap-1 max-w-full text-xs bg-muted/60 hover:bg-muted px-2 py-1.5"
                      title={`Descargar ${a.nombre}`} data-testid="mail-adjunto-descargar"
                    >
                      <Paperclip className="w-3 h-3 shrink-0 text-[#2E6E5E]" />
                      <span className="truncate max-w-[10rem]">{a.nombre}</span>
                      {a.size ? <span className="text-muted-foreground shrink-0">{fmtSize(a.size)}</span> : null}
                    </button>
                    {admin && (
                      <button
                        type="button" onClick={() => guardarEnDrive(i, a)}
                        disabled={guardandoDrive === i}
                        className="inline-flex items-center gap-1 px-2 bg-[#2E6E5E]/10 hover:bg-[#2E6E5E]/20 border-l border-border"
                        title="Guardar en ClinicPilot Drive (carpeta Correo)"
                        data-testid="mail-adjunto-a-drive"
                      >
                        {guardandoDrive === i
                          ? <Loader2 className="w-3.5 h-3.5 animate-spin text-[#2E6E5E]" />
                          : <HardDrive className="w-3.5 h-3.5 text-[#2E6E5E]" />}
                        <span className="text-xs font-medium text-[#2E6E5E]">Drive</span>
                      </button>
                    )}
                  </span>
                ) : (
                  <span key={i} className="inline-flex items-center gap-1 max-w-full text-xs bg-muted/60 rounded px-2 py-1.5">
                    <Paperclip className="w-3 h-3 shrink-0" />
                    <span className="truncate max-w-[11rem]">{a.nombre}</span>
                  </span>
                )
              ))}
            </div>
          )}
          <div className="p-4">
            {abierto.body_html
              ? <CuerpoHtml html={abierto.body_html} />
              : (
                <pre className="whitespace-pre-wrap break-words font-sans text-sm text-[#0B1220]" data-testid="mail-body-text">
                  {abierto.body_text || '(sin contenido)'}
                </pre>
              )}
          </div>
        </div>
      ) : (
        /* -------------------------- Lista -------------------------- */
        <div className="cjo-card overflow-hidden" data-testid="mail-list">
          <div className="px-3 py-2 border-b border-border flex items-center gap-2 flex-wrap">
            <div className="flex rounded-lg border border-border overflow-hidden">
              <button type="button" onClick={() => setCarpeta('in')}
                className={`px-3 py-1.5 text-sm flex items-center gap-1 ${carpeta === 'in' ? 'bg-[#2E6E5E] text-white' : 'bg-white dark:bg-card hover:bg-muted/40'}`}
                data-testid="mail-folder-inbox">
                <Inbox className="w-3.5 h-3.5" /> Recibidos
                {sinLeerSel > 0 && (
                  <span className={`ml-1 text-[10px] font-semibold rounded-full px-1.5 py-0.5 ${carpeta === 'in' ? 'bg-white/20 text-white' : 'bg-[#2E6E5E] text-white'}`}
                    data-testid="mail-unread-count">
                    {sinLeerSel}
                  </span>
                )}
              </button>
              <button type="button" onClick={() => setCarpeta('out')}
                className={`px-3 py-1.5 text-sm flex items-center gap-1 ${carpeta === 'out' ? 'bg-[#2E6E5E] text-white' : 'bg-white dark:bg-card hover:bg-muted/40'}`}
                data-testid="mail-folder-sent">
                <Send className="w-3.5 h-3.5" /> Enviados
              </button>
            </div>
            <div className="relative flex-1 min-w-[180px]">
              <Search className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input className="pl-8 h-9" placeholder="Buscar por remitente o asunto…"
                value={q} onChange={(e) => setQ(e.target.value)} data-testid="mail-search" />
            </div>
            <span className="text-xs text-muted-foreground">{total} mensajes</span>
            {marcados.length > 0 && (
              <div className="flex flex-wrap items-center gap-1.5 w-full sm:w-auto sm:ml-auto" data-testid="mail-seleccion-bar">
                <span className="text-xs font-semibold text-[#13231E]">{marcados.length} selecc.</span>
                {carpeta === 'in' && (
                  <>
                    <Button size="sm" variant="outline" className="h-8"
                      onClick={() => marcarLeido(marcados, true)}
                      title="Marcar como leídos" data-testid="mail-marcar-leidos">
                      <MailCheck className="w-3.5 h-3.5 mr-1" /> Leídos
                    </Button>
                    <Button size="sm" variant="outline" className="h-8"
                      onClick={() => marcarLeido(marcados, false)}
                      title="Marcar como no leídos" data-testid="mail-marcar-no-leidos">
                      <MailOpen className="w-3.5 h-3.5 mr-1" /> No leídos
                    </Button>
                  </>
                )}
                <Button size="sm" variant="outline"
                  className="h-8 text-destructive border-destructive/40 hover:bg-destructive/10"
                  onClick={() => eliminarIds(marcados)} data-testid="mail-eliminar-marcados">
                  <Trash2 className="w-3.5 h-3.5 mr-1" /> Eliminar
                </Button>
                <Button size="sm" variant="ghost" className="h-8" onClick={() => setMarcados([])}>
                  Cancelar
                </Button>
              </div>
            )}
          </div>

          {cargando && items.length === 0 ? (
            <div className="py-10 text-center text-muted-foreground">
              <Loader2 className="w-5 h-5 animate-spin inline mr-2" /> Cargando…
            </div>
          ) : items.length === 0 ? (
            <EmptyState
              icon={Inbox}
              title={carpeta === 'in' ? 'Bandeja vacía' : 'Nada enviado todavía'}
              description={cuentasSel.some((c) => c.last_sync)
                ? 'No hay mensajes que coincidan.'
                : 'Pulsa "Actualizar" para traer el correo del buzón.'}
            />
          ) : (
            <ul className="divide-y divide-border">
              {items.map((m) => {
                const noLeido = carpeta === 'in' && !m.read;
                return (
                  <li key={m.id}
                    className={`flex items-center ${cuentaUi(m.account).barra} ${noLeido ? cuentaUi(m.account).fondoNoLeido : ''} ${marcados.includes(m.id) ? 'bg-[#2E6E5E]/[0.10]' : ''}`}>
                    <input
                      type="checkbox"
                      className="accent-[#2E6E5E] w-4 h-4 ml-3 shrink-0 cursor-pointer"
                      checked={marcados.includes(m.id)}
                      onChange={() => toggleMarcado(m.id)}
                      aria-label="Seleccionar correo"
                      data-testid="mail-row-check"
                    />
                    <button type="button" onClick={() => abrir(m)}
                      className="flex-1 min-w-0 text-left px-2 sm:px-3 py-2.5 flex items-center gap-2 sm:gap-3 hover:bg-muted/30"
                      data-testid="mail-row">
                      <span className={`w-2 h-2 rounded-full shrink-0 ${noLeido ? cuentaUi(m.account).punto : 'bg-transparent'}`} />
                      {/* Con los dos buzones a la vez, la marca dice de cuál es
                          cada correo mejor que el chip de texto de antes. */}
                      {seleccionadas.length > 1 && (
                        <MarcaBuzon clave={m.account} size={28}
                          cuenta={cuentas.find((c) => c.key === m.account)}
                          data-testid="mail-row-cuenta" />
                      )}
                      <span className={`w-24 sm:w-44 shrink-0 truncate text-sm ${noLeido ? 'font-semibold text-[#0B1220]' : 'text-[#13231E]'}`}>
                        {carpeta === 'out' ? m.to : (m.from_name || m.from_email)}
                      </span>
                      <span className="flex-1 min-w-0 truncate text-sm">
                        <span className={noLeido ? 'font-semibold text-[#0B1220]' : 'text-[#13231E]'}>
                          {m.subject || '(sin asunto)'}
                        </span>
                        {m.snippet && (
                          <span className="hidden sm:inline text-muted-foreground"> — {m.snippet}</span>
                        )}
                      </span>
                      {(m.attachments || []).length > 0 && (
                        <Paperclip className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                      )}
                      <span className="text-xs text-muted-foreground shrink-0 w-14 text-right">
                        {fechaCorta(m.date)}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}

      <ComposeDialog
        open={compose}
        onOpenChange={setCompose}
        cuentas={cuentas}
        seleccionadas={seleccionadas}
        admin={admin}
        replyTo={replyTo}
        forwardOf={forwardOf}
        onSent={() => {
          setAbierto(null);
          // Cambiar de carpeta ya recarga la lista; si ya estabas en
          // Enviados hay que recargarla a mano.
          if (carpeta !== 'out') setCarpeta('out'); else cargarLista();
        }}
      />
      <FirmaDialog
        open={firmaOpen}
        onOpenChange={setFirmaOpen}
        cuentas={cuentasSel.length > 0 ? cuentasSel : cuentas}
        onSaved={cargarCuentas}
      />
    </div>
  );
}
